// src/components/Dashboard.js
import React, { useEffect, useState, useRef } from 'react';
import './Dashboard.css';
import axios from 'axios';

// API endpoints
const EMPLOYEES_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/employees';
const DEPARTMENTS_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/departments';
const PROJECTS_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/projects';
const COMMENTS_GET_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/comments';
const COMMENTS_POST_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/commentpost';

// Comment Modal Component
function CommentModal({ isOpen, onClose, employee, onSubmit, commentText, setCommentText, submitting }) {
  if (!isOpen || !employee) return null;

  return (
    <div className="modal-overlay">
      <div className="comment-modal">
        <div className="modal-header">
          <h2>{employee.employeeName}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="employee-details">
          <div className="detail-row">
            <div className="detail-item">
              <span className="detail-label">Zoho ID</span>
              <span className="detail-value">{employee.zohoId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Department</span>
              <span className="detail-value">{employee.department}</span>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">{employee.statusDisplay}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Cost</span>
              <span className="detail-value">${employee.costString}</span>
            </div>
          </div>
        </div>
        
        <div className="comments-section">
          <h3>Comments History</h3>
          <div className="comments-list">
            {employee.comments && employee.comments.length > 0 ? (
              employee.comments.map((comment, index) => (
                <div key={index} className="comment-item">
                  <div className="comment-text">{comment.text}</div>
                  <div className="comment-meta">
                    <span className="comment-author">{comment.authorName || comment.author || 'Anonymous'}</span>
                    <span className="comment-timestamp">
                      {new Date(comment.timestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-comments">No comments yet</div>
            )}
          </div>
        </div>
        
        <div className="add-comment-section">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            rows={4}
          />
          <button 
            className="add-comment-button" 
            onClick={onSubmit}
            disabled={submitting || !commentText.trim()}
          >
            {submitting ? 'Adding...' : 'Add Comment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [employeeData, setEmployeeData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [userInfo, setUserInfo] = useState({
    name: 'User',
    email: '',
    loading: true,
    accessLevel: 'primary',
    userDepartment: '',
    managedProjects: []
  });
  
  // Hover comment state
  const [hoverEmployee, setHoverEmployee] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const tooltipUpdateTimerRef = useRef(null);
  const tooltipRef = useRef(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  
  // Track which dropdown is currently open
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Filter states - updated to use arrays for multi-select
  const [filters, setFilters] = useState({
    department: ['All'],
    status: ['All'],
    clientName: ['All'],
    businessUnit: ['All'],
    projectName: ['All'],
    timesheetAging: 'All'
  });

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    department: ['All'],
    status: ['All'],
    clientName: ['All'],
    businessUnit: ['All'],
    projectName: ['All'],
    timesheetAging: [
      { value: 'All', label: 'All' },
      { value: 'gt10', label: '> 10 Days' },
      { value: 'gt30', label: '> 30 Days' },
      { value: 'gt60', label: '> 60 Days' },
      { value: 'gt90', label: '> 90 Days' },
      { value: 'missing', label: 'Missing Last Week' }
    ]
  });

  // Save comments to localStorage
  const saveCommentsToLocalStorage = (comments) => {
    try {
      localStorage.setItem('employee_comments', JSON.stringify(comments));
    } catch (error) {
      console.error('Error saving comments to localStorage:', error);
    }
  };

  // Load comments from localStorage
  const loadCommentsFromLocalStorage = () => {
    try {
      const savedComments = localStorage.getItem('employee_comments');
      return savedComments ? JSON.parse(savedComments) : {};
    } catch (error) {
      console.error('Error loading comments from localStorage:', error);
      return {};
    }
  };

  // Format cost with commas for thousands
  const formatCost = (costString) => {
    if (!costString) return '0';
    const costNumber = parseFloat(costString);
    if (isNaN(costNumber)) return costString;
    return costNumber.toLocaleString('en-US');
  };

  // Toggle dropdown open/close
  const toggleDropdown = (dropdownName) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchText('');
    const resetFilters = {
      department: ['All'],
      status: ['All'],
      clientName: ['All'],
      businessUnit: ['All'],
      projectName: ['All'],
      timesheetAging: 'All'
    };
    setFilters(resetFilters);
    
    // Apply reset based on access level
    if (userInfo.accessLevel === 'tertiary') {
      applyTertiaryFiltering();
    } else if (userInfo.accessLevel === 'secondary') {
      applySecondaryFiltering(resetFilters, '');
    } else {
      // For primary access, just reset to show all employees
      setFilteredData(employeeData);
      setEmployeeCount(employeeData.length);
    }
    
    // Close any open dropdowns
    setOpenDropdown(null);
  };

  // Function to handle checkbox change
  const handleCheckboxChange = (name, value, isChecked) => {
    let newValues = [...filters[name]];
    
    if (value === 'All') {
      newValues = isChecked ? ['All'] : [];
    } else if (isChecked) {
      newValues = newValues.filter(v => v !== 'All');
      newValues.push(value);
    } else {
      newValues = newValues.filter(v => v !== value);
      if (newValues.length === 0) {
        newValues = ['All'];
      }
    }
    
    const newFilters = { ...filters, [name]: newValues };
    setFilters(newFilters);
    
    // For tertiary users, we need to apply filters to the tertiary-filtered data
    if (userInfo.accessLevel === 'tertiary') {
      applyRegularFiltersToTertiaryData(newFilters);
    } else if (userInfo.accessLevel === 'secondary') {
      applySecondaryFiltering(newFilters);
    } else {
      applyFiltersAndSearch(searchText, newFilters);
    }
  };

  // Parse JWT token 
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return {};
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('access_level');
    localStorage.removeItem('user_department');
    localStorage.removeItem('managed_projects');
    localStorage.removeItem('managed_project_ids');
    localStorage.removeItem('employee_comments');
    
    window.location.href = '/';
  };

  // Handle search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    
    // For tertiary users, apply search to tertiary filtered data
    if (userInfo.accessLevel === 'tertiary') {
      const newFilters = { ...filters };
      setFilters(newFilters);
      applyRegularFiltersToTertiaryData(newFilters, value);
    } else if (userInfo.accessLevel === 'secondary') {
      applySecondaryFiltering(filters, value);
    } else {
      applyFiltersAndSearch(value, filters);
    }
  };

  // Fetch comments for an employee
  const fetchEmployeeComments = async (employeeId) => {
    try {
      setLoadingComments(true);
      const accessToken = localStorage.getItem('access_token');
      
      if (!accessToken) {
        throw new Error('No access token found');
      }
      
      const response = await fetch(`${COMMENTS_GET_API_URL}?employee_id=${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      
      const commentsData = await response.json();
      
      if (commentsData && Array.isArray(commentsData) && commentsData.length > 0) {
        const employeeComments = commentsData.find(c => c.employee_id === employeeId);
        if (employeeComments && employeeComments.comments && Array.isArray(employeeComments.comments)) {
          // Format comments for display
          const formattedComments = employeeComments.comments.map(comment => {
            // If it's already an object with text property, leave it as is
            if (typeof comment === 'object' && comment.text) {
              return comment;
            }
            
            // Parse the comment string if it has the format: text|email|timestamp
            if (typeof comment === 'string' && comment.includes('|')) {
              const parts = comment.split('|');
              if (parts.length >= 3) {
                return {
                  text: parts[0],
                  author: parts[1],
                  authorName: parts[1],
                  timestamp: parts[2]
                };
              }
            }
            
            // Fallback for regular string comments
            return {
              text: comment,
              author: userInfo.email || 'Anonymous',
              authorName: userInfo.name || userInfo.email || 'Anonymous',
              timestamp: new Date().toISOString()
            };
          });
          
          return formattedComments;
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    } finally {
      setLoadingComments(false);
    }
  };

  // Handle open comment modal with comment fetch
  const handleOpenCommentModal = async (employee) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
    setCommentText('');
    
    // Try to fetch the latest comments for this employee
    try {
      const latestComments = await fetchEmployeeComments(employee.employeeNumber);
      
      if (latestComments && latestComments.length > 0) {
        // Update the employee with latest comments
        const updatedEmployee = { ...employee, comments: latestComments };
        setSelectedEmployee(updatedEmployee);
        
        // Also update in the employee data and filtered data
        const updatedEmployeeData = employeeData.map(emp => 
          emp.employeeNumber === employee.employeeNumber ? updatedEmployee : emp
        );
        setEmployeeData(updatedEmployeeData);
        
        const updatedFilteredData = filteredData.map(emp => 
          emp.employeeNumber === employee.employeeNumber ? updatedEmployee : emp
        );
        setFilteredData(updatedFilteredData);
        
        // Update localStorage
        const savedComments = loadCommentsFromLocalStorage();
        savedComments[employee.employeeNumber] = latestComments;
        saveCommentsToLocalStorage(savedComments);
      }
    } catch (error) {
      console.error('Error fetching latest comments:', error);
      // Continue with existing comments if we can't fetch the latest
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  // Handle submit comment
  const handleSubmitComment = async () => {
    if (!commentText.trim() || !selectedEmployee) return;
  
    setCommentSubmitting(true);
    
    try {
      const accessToken = localStorage.getItem('access_token');
      
      if (!accessToken) {
        throw new Error('No access token found');
      }
      
      // Create a combined string with commentText + user email + timestamp
      const timestamp = new Date().toISOString();
      const combinedCommentString = `${commentText.trim()}|${userInfo.email}|${timestamp}`;
      
      // Format the comment for display immediately
      const formattedNewComment = {
        text: commentText.trim(),
        timestamp: timestamp,
        author: userInfo.email,
        authorName: userInfo.name || userInfo.email
      };
      
      // Update selected employee comments
      const updatedComments = [...(selectedEmployee.comments || []), formattedNewComment];
      const updatedEmployee = { ...selectedEmployee, comments: updatedComments };
      setSelectedEmployee(updatedEmployee);
      
      // Update employee in employee data
      const updatedEmployeeData = employeeData.map(emp => 
        emp.employeeNumber === selectedEmployee.employeeNumber ? updatedEmployee : emp
      );
      setEmployeeData(updatedEmployeeData);
      
      // Update filtered data
      const updatedFilteredData = filteredData.map(emp => 
        emp.employeeNumber === selectedEmployee.employeeNumber ? updatedEmployee : emp
      );
      setFilteredData(updatedFilteredData);
      
      // Also update in localStorage for backup
      const savedComments = loadCommentsFromLocalStorage();
      savedComments[selectedEmployee.employeeNumber] = updatedComments;
      saveCommentsToLocalStorage(savedComments);
      
      // Clear the comment input
      setCommentText('');
      
      // First, try to get existing comments from API
      let existingComments = [];
      
      try {
        const getResponse = await fetch(`${COMMENTS_GET_API_URL}?employee_id=${selectedEmployee.employeeNumber}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          mode: 'cors'
        });
        
        if (getResponse.ok) {
          const commentsData = await getResponse.json();
          if (commentsData && Array.isArray(commentsData) && commentsData.length > 0) {
            const employeeComments = commentsData.find(c => c.employee_id === selectedEmployee.employeeNumber);
            if (employeeComments && employeeComments.comments && Array.isArray(employeeComments.comments)) {
              existingComments = employeeComments.comments;
            }
          }
        }
      } catch (error) {
        console.log('Error fetching existing comments, using local comments instead');
        // If we can't fetch, use what we have from the employee object
        if (selectedEmployee.comments && Array.isArray(selectedEmployee.comments)) {
          existingComments = selectedEmployee.comments
            .filter(c => c.text) // Filter out any malformed comments
            .map(c => typeof c === 'object' ? c.text : c);
        }
      }
      
      // Make sure we don't duplicate the comment we just added
      if (existingComments.includes(commentText.trim())) {
        existingComments = existingComments.filter(c => c !== commentText.trim());
      }
      
      // Add the new combined comment string to existing ones
      const allComments = [...existingComments, combinedCommentString];
      
      // Prepare payload with ALL comments (existing + new)
      const payload = {
        employee_id: selectedEmployee.employeeNumber,
        comments: allComments
      };
      
      console.log('Sending comment payload:', payload);
      
      // Now try to post to API
      try {
        // Use fetch instead of axios for more control over CORS
        const response = await fetch(COMMENTS_POST_API_URL, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload),
          mode: 'cors'
        });
        
        if (response.ok) {
          console.log('Comment posted successfully');
          
          // After successful post, re-fetch the comments to ensure sync
          const latestComments = await fetchEmployeeComments(selectedEmployee.employeeNumber);
          if (latestComments && latestComments.length > 0) {
            const syncedEmployee = { ...selectedEmployee, comments: latestComments };
            setSelectedEmployee(syncedEmployee);
            
            // Update employee data and filtered data too
            const syncedEmployeeData = employeeData.map(emp => 
              emp.employeeNumber === selectedEmployee.employeeNumber ? syncedEmployee : emp
            );
            setEmployeeData(syncedEmployeeData);
            
            const syncedFilteredData = filteredData.map(emp => 
              emp.employeeNumber === selectedEmployee.employeeNumber ? syncedEmployee : emp
            );
            setFilteredData(syncedFilteredData);
            
            // Update localStorage with synced comments
            const savedComments = loadCommentsFromLocalStorage();
            savedComments[selectedEmployee.employeeNumber] = latestComments;
            saveCommentsToLocalStorage(savedComments);
          }
        } else {
          console.error('Failed to post comment:', response.statusText);
        }
      } catch (postError) {
        console.error('Error posting comment:', postError);
      }
    } catch (error) {
      console.error('Error in comment submission:', error);
    } finally {
      setCommentSubmitting(false);
    }
      
  };

  // Handle comment tooltip mouse events - Fixed tooltip positioning
  const handleMouseOver = (event, employee) => {
    if (!employee.comments || employee.comments.length === 0) return;
    
    const buttonRect = event.currentTarget.getBoundingClientRect();
    
    // Calculate tooltip position to ensure it stays within view
    // Place on left side of button if near right edge of screen
    const windowWidth = window.innerWidth;
    const tooltipWidth = 300; // Approximate width of tooltip
    const isNearRightEdge = (buttonRect.right + tooltipWidth) > windowWidth;
    
    let xPos;
    if (isNearRightEdge) {
      // Place to the left of the button
      xPos = buttonRect.left - tooltipWidth - 15;
    } else {
      // Place to the right of the button
      xPos = buttonRect.right + 15;
    }
    
    lastPositionRef.current = {
      x: xPos,
      y: buttonRect.top
    };
    
    setHoverEmployee(employee);
    
    // Clear any existing timer
    if (tooltipUpdateTimerRef.current) {
      clearTimeout(tooltipUpdateTimerRef.current);
    }
    
    tooltipUpdateTimerRef.current = setTimeout(() => {
      setHoverPosition({
        x: lastPositionRef.current.x,
        y: lastPositionRef.current.y
      });
      setTooltipVisible(true);
    }, 300);
  };

  const handleMouseMove = (event) => {
    // Don't update position on mouse move to keep tooltip stable
  };

  const handleMouseOut = () => {
    if (tooltipUpdateTimerRef.current) {
      clearTimeout(tooltipUpdateTimerRef.current);
      tooltipUpdateTimerRef.current = null;
    }
    
    setTooltipVisible(false);
    setHoverEmployee(null);
  };
  
  // Special function to handle secondary access filtering
  const applySecondaryFiltering = (currentFilters, search = searchText) => {
    console.log("SECONDARY FILTERING FUNCTION CALLED");
    
    if (
      userInfo.accessLevel !== 'secondary' || 
      employeeData.length === 0 ||
      !userInfo.userDepartment
    ) {
      console.log("Skipping secondary filtering due to missing data");
      return;
    }
    
    console.log("Starting secondary filtering with department:", userInfo.userDepartment);
    console.log("Total employees before filtering:", employeeData.length);
    
    // First filter by user's department
    let filtered = employeeData.filter(employee => 
      employee.department === userInfo.userDepartment
    );
    
    console.log(`Found ${filtered.length} employees for department: ${userInfo.userDepartment}`);
    
    // Then apply search filter if any
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(employee => 
        (employee.employeeName && employee.employeeName.toLowerCase().includes(searchLower)) ||
        (employee.zohoId && employee.zohoId.toLowerCase().includes(searchLower)) ||
        (employee.department && employee.department.toLowerCase().includes(searchLower)) ||
        (employee.businessUnit && employee.businessUnit.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply department filter - for consistency, though it's redundant for secondary
    if (currentFilters.department.length && !currentFilters.department.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.department.includes(employee.department)
      );
    }
    
    // Apply status filter
    if (currentFilters.status.length && !currentFilters.status.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.status.includes(employee.statusDisplay)
      );
    }
    
    // Apply business unit filter
    if (currentFilters.businessUnit.length && !currentFilters.businessUnit.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.businessUnit.includes(employee.businessUnit)
      );
    }
    
    // Apply client filter
    if (currentFilters.clientName.length && !currentFilters.clientName.includes('All')) {
      filtered = filtered.filter(employee => 
        employee.clientNames && Array.isArray(employee.clientNames) &&
        employee.clientNames.some(client => 
          currentFilters.clientName.includes(client)
        )
      );
    }
    
    // Apply project filter
    if (currentFilters.projectName.length && !currentFilters.projectName.includes('All')) {
      filtered = filtered.filter(employee => 
        employee.projectNames && Array.isArray(employee.projectNames) &&
        employee.projectNames.some(project => 
          currentFilters.projectName.includes(project)
        )
      );
    }
    
    // Apply timesheet aging filter
    if (currentFilters.timesheetAging !== 'All') {
      switch (currentFilters.timesheetAging) {
        case 'gt10':
          filtered = filtered.filter(employee => employee.timesheetAgingGt10Days);
          break;
        case 'gt30':
          filtered = filtered.filter(employee => employee.timesheetAgingGt30Days);
          break;
        case 'gt60':
          filtered = filtered.filter(employee => employee.timesheetAgingGt60Days);
          break;
        case 'gt90':
          filtered = filtered.filter(employee => employee.timesheetAgingGt90Days);
          break;
        case 'missing':
          filtered = filtered.filter(employee => employee.missingLastWeekTimesheet);
          break;
        default:
          break;
      }
    }
    
    setFilteredData(filtered);
    setEmployeeCount(filtered.length);
  };
  
  // Function for tertiary users - apply regular filters after tertiary filtering
  const applyRegularFiltersToTertiaryData = (currentFilters, search = searchText) => {
    console.log("Applying regular filters to tertiary user data");
    
    // Get the base tertiary-filtered data first
    let tertiaryData = [];
    let managedProjectIds = [];
    
    try {
      managedProjectIds = JSON.parse(localStorage.getItem('managed_project_ids') || '[]');
    } catch (err) {
      console.error("Error parsing managed project IDs:", err);
    }
    
    // Filter employees based on managed project IDs
    tertiaryData = employeeData.filter(employee => {
      if (!employee.time_logs || !Array.isArray(employee.time_logs)) {
        return false;
      }
      
      return employee.time_logs.some(log => {
        if (!log.project_id) return false;
        return managedProjectIds.includes(log.project_id);
      });
    });
    
    if (tertiaryData.length === 0) {
      tertiaryData = [...employeeData]; // Fallback if no matches
    }
    
    // Now apply regular filters to the tertiary data
    let filtered = [...tertiaryData];
    
    // Apply search filter
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(employee => 
        (employee.employeeName && employee.employeeName.toLowerCase().includes(searchLower)) ||
        (employee.zohoId && employee.zohoId.toLowerCase().includes(searchLower)) ||
        (employee.department && employee.department.toLowerCase().includes(searchLower)) ||
        (employee.businessUnit && employee.businessUnit.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply department filter
    if (currentFilters.department.length && !currentFilters.department.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.department.includes(employee.department)
      );
    }
    
    // Apply status filter
    if (currentFilters.status.length && !currentFilters.status.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.status.includes(employee.statusDisplay)
      );
    }
    
    // Apply business unit filter
    if (currentFilters.businessUnit.length && !currentFilters.businessUnit.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.businessUnit.includes(employee.businessUnit)
      );
    }
    
    // Apply client filter
    if (currentFilters.clientName.length && !currentFilters.clientName.includes('All')) {
      filtered = filtered.filter(employee => 
        employee.clientNames && Array.isArray(employee.clientNames) &&
        employee.clientNames.some(client => 
          currentFilters.clientName.includes(client)
        )
      );
    }
    
    // Apply project filter
    if (currentFilters.projectName.length && !currentFilters.projectName.includes('All')) {
      filtered = filtered.filter(employee => 
        employee.projectNames && Array.isArray(employee.projectNames) &&
        employee.projectNames.some(project => 
          currentFilters.projectName.includes(project)
        )
      );
    }
    
    // Apply timesheet aging filter
    if (currentFilters.timesheetAging !== 'All') {
      switch (currentFilters.timesheetAging) {
        case 'gt10':
          filtered = filtered.filter(employee => employee.timesheetAgingGt10Days);
          break;
        case 'gt30':
          filtered = filtered.filter(employee => employee.timesheetAgingGt30Days);
          break;
        case 'gt60':
          filtered = filtered.filter(employee => employee.timesheetAgingGt60Days);
          break;
        case 'gt90':
          filtered = filtered.filter(employee => employee.timesheetAgingGt90Days);
          break;
        case 'missing':
          filtered = filtered.filter(employee => employee.missingLastWeekTimesheet);
          break;
        default:
          break;
      }
    }
    
    setFilteredData(filtered);
    setEmployeeCount(filtered.length);
  };

  // Special function to handle tertiary access filtering - FIXED to retain filter results
  const applyTertiaryFiltering = () => {
    console.log("TERTIARY FILTERING FUNCTION CALLED");
    
    if (
      userInfo.accessLevel !== 'tertiary' || 
      employeeData.length === 0
    ) {
      console.log("Skipping tertiary filtering due to missing data");
      return;
    }
    
    console.log("Starting tertiary filtering with managed projects");
    console.log("Total employees before filtering:", employeeData.length);
    
    // Get managed project IDs from localStorage
    let managedProjectIds = [];
    try {
      managedProjectIds = JSON.parse(localStorage.getItem('managed_project_ids') || '[]');
      console.log("Managed project IDs:", managedProjectIds);
    } catch (err) {
      console.error("Error parsing managed project IDs:", err);
    }
    
    if (managedProjectIds.length === 0) {
      console.log("No managed project IDs found, showing all employees");
      // Apply current filters after tertiary filtering
      applyRegularFiltersToTertiaryData(filters, searchText);
      return;
    }
    
    // Apply regular filters after tertiary filtering
    applyRegularFiltersToTertiaryData(filters, searchText);
  };

  // Add this effect to trigger proper filtering when data changes
  useEffect(() => {
    if (userInfo.accessLevel === 'tertiary' && !userInfo.loading) {
      console.log("User info or employee data changed, applying tertiary filtering");
      applyTertiaryFiltering();
    } else if (userInfo.accessLevel === 'secondary' && !userInfo.loading) {
      console.log("User info or employee data changed, applying secondary filtering");
      applySecondaryFiltering(filters);
    } else if (!userInfo.loading) {
      console.log("User info or employee data changed, applying primary filtering");
      applyFiltersAndSearch(searchText, filters);
    }
  }, [employeeData, userInfo]);

  // Apply filters and search based on current filter state and search text
  const applyFiltersAndSearch = (search, currentFilters) => {
    // If tertiary or secondary access, use the special filtering
    if (userInfo.accessLevel === 'tertiary') {
      console.log("Skip regular filtering for tertiary access, applying tertiary filtering instead");
      applyTertiaryFiltering();
      return;
    } else if (userInfo.accessLevel === 'secondary') {
      console.log("Skip regular filtering for secondary access, applying secondary filtering instead");
      applySecondaryFiltering(currentFilters, search);
      return;
    }
    
    let filtered = [...employeeData];
    
    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(employee => 
        (employee.employeeName && employee.employeeName.toLowerCase().includes(searchLower)) ||
        (employee.zohoId && employee.zohoId.toLowerCase().includes(searchLower)) ||
        (employee.department && employee.department.toLowerCase().includes(searchLower)) ||
        (employee.businessUnit && employee.businessUnit.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply department filter if any
    if (currentFilters.department.length && !currentFilters.department.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.department.includes(employee.department)
      );
    }
    
    // Apply status filter if any
    if (currentFilters.status.length && !currentFilters.status.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.status.includes(employee.statusDisplay)
      );
    }
    
    // Apply business unit filter if any
    if (currentFilters.businessUnit.length && !currentFilters.businessUnit.includes('All')) {
      filtered = filtered.filter(employee => 
        currentFilters.businessUnit.includes(employee.businessUnit)
      );
    }
    
    // Apply client filter if any
    if (currentFilters.clientName.length && !currentFilters.clientName.includes('All')) {
      filtered = filtered.filter(employee => 
        employee.clientNames && Array.isArray(employee.clientNames) &&
        employee.clientNames.some(client => 
          currentFilters.clientName.includes(client)
        )
      );
    }
    
    // Apply project filter if any
    if (currentFilters.projectName.length && !currentFilters.projectName.includes('All')) {
      filtered = filtered.filter(employee => 
        employee.projectNames && Array.isArray(employee.projectNames) &&
        employee.projectNames.some(project => 
          currentFilters.projectName.includes(project)
        )
      );
    }
    
    // Apply timesheet aging filter if any
    if (currentFilters.timesheetAging !== 'All') {
      switch (currentFilters.timesheetAging) {
        case 'gt10':
          filtered = filtered.filter(employee => employee.timesheetAgingGt10Days);
          break;
        case 'gt30':
          filtered = filtered.filter(employee => employee.timesheetAgingGt30Days);
          break;
        case 'gt60':
          filtered = filtered.filter(employee => employee.timesheetAgingGt60Days);
          break;
        case 'gt90':
          filtered = filtered.filter(employee => employee.timesheetAgingGt90Days);
          break;
        case 'missing':
          filtered = filtered.filter(employee => employee.missingLastWeekTimesheet);
          break;
        default:
          break;
      }
    }
    
    setFilteredData(filtered);
    setEmployeeCount(filtered.length);
  };

  // Handle CSV download
  const handleDownloadCSV = () => {
    if (filteredData.length === 0) return;
    
    // Prepare CSV content
    let csvContent = 'Employee Name,Zoho ID,Department,Status,Business Unit,Client,Project,Month Billable,Month Non-Billable,Cost\n';
    
    filteredData.forEach(employee => {
      const clientNames = employee.clientNames && employee.clientNames.length > 0 
        ? `"${employee.clientNames.join(', ')}"` 
        : 'N/A';
      
      const projectNames = employee.projectNames && employee.projectNames.length > 0 
        ? `"${employee.projectNames.join(', ')}"` 
        : 'N/A';
      
      csvContent += `${employee.employeeName || ''},${employee.zohoId || ''},${employee.department || ''},${employee.statusDisplay || ''},${employee.businessUnit || ''},${clientNames},${projectNames},${employee.lastMonthBillableHours || 0},${employee.lastMonthNonBillableHours || 0},$${employee.costString || '0'}\n`;
    });
    
    // Create a Blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'employee_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    // Try to get user information from token
    const getUserInfo = async () => {
      try {
        const idToken = localStorage.getItem('id_token');
        const accessLevel = localStorage.getItem('access_level') || 'primary';
        const userDepartment = localStorage.getItem('user_department') || '';
        const userEmail = localStorage.getItem('user_email') || '';
        let managedProjects = [];
        
        try {
          managedProjects = JSON.parse(localStorage.getItem('managed_projects') || '[]');
        } catch (err) {
          console.error("Error parsing managed projects:", err);
          managedProjects = [];
        }
        
        if (!idToken) {
          setUserInfo({
            name: 'User',
            email: userEmail,
            loading: false,
            accessLevel: accessLevel,
            userDepartment: userDepartment,
            managedProjects: managedProjects
          });
          return;
        }
        
        const payload = parseJwt(idToken);
        
        setUserInfo({
          name: payload.name || payload.email || 'User',
          email: payload.email || userEmail,
          loading: false,
          accessLevel: accessLevel,
          userDepartment: userDepartment,
          managedProjects: managedProjects
        });
      } catch (error) {
        console.error('Error getting user info', error);
        setUserInfo({
          name: 'User',
          email: localStorage.getItem('user_email') || '',
          loading: false,
          accessLevel: localStorage.getItem('access_level') || 'primary',
          userDepartment: localStorage.getItem('user_department') || '',
          managedProjects: JSON.parse(localStorage.getItem('managed_projects') || '[]')
        });
      }
    };
    
    getUserInfo();
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const accessToken = localStorage.getItem('access_token');
        
        if (!accessToken) {
          throw new Error('No access token found. Please log in again.');
        }
        
        const headers = {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        };
        
        console.log('Fetching employee data...');
        
        // Fetch employees, departments, and projects
        const [employeesResponse, departmentsResponse, projectsResponse] = await Promise.all([
          axios.get(EMPLOYEES_API_URL, { headers }),
          axios.get(DEPARTMENTS_API_URL, { headers }),
          axios.get(PROJECTS_API_URL, { headers })
        ]);
        
        const employees = employeesResponse.data;
        const departments = departmentsResponse.data;
        const projects = projectsResponse.data;
        
        console.log(`Retrieved ${employees.length} employees, ${departments.length} departments, ${projects.length} projects`);
        
        const savedComments = loadCommentsFromLocalStorage();
        
        setDepartmentData(departments);
        setProjectData(projects);
        
        // Extract unique department names, status values, client names, etc. for filters
        const departmentOptions = ['All'];
        const statusOptions = ['All'];
        const businessUnitOptions = ['All'];
        const clientNameOptions = ['All'];
        const projectNameOptions = ['All'];
        
        // Transform employees data to include all required fields
        const transformedEmployees = await Promise.all(employees.map(async (employee) => {
          const employeeId = employee.id || '';
          let comments = [];
          
          if (savedComments[employeeId]) {
            comments = savedComments[employeeId].map(commentText => {
              if (typeof commentText === 'object' && commentText.text) {
                return commentText;
              }
              return {
                text: commentText,
                author: 'Unknown',
                timestamp: new Date().toISOString()
              };
            });
          }
          
          // Get client names and project names from time_logs
          const clientNames = [];
          const projectNames = [];
          
          if (employee.time_logs && Array.isArray(employee.time_logs)) {
            employee.time_logs.forEach(log => {
              if (log.client_name && !clientNames.includes(log.client_name)) {
                clientNames.push(log.client_name);
                
                // Add to client filter options
                if (!clientNameOptions.includes(log.client_name)) {
                  clientNameOptions.push(log.client_name);
                }
              }
              
              if (log.project_name && !projectNames.includes(log.project_name)) {
                projectNames.push(log.project_name);
                
                // Add to project filter options
                if (!projectNameOptions.includes(log.project_name)) {
                  projectNameOptions.push(log.project_name);
                }
              }
            });
          }
          
          const lastMonthBillableHours = Number(employee.last_month_billable_hours || 0);
          const lastMonthNonBillableHours = Number(employee.last_month_non_billable_hours || 0);
          
          // Status logic based on last month hours
          let statusDisplay = '';
          if (employee.timesheet_aging_gt_10_days) {
            statusDisplay = 'No Timesheet Filled';
          } else {
            statusDisplay = 'Non Billable';
          }
          
          // Add to department, status, and business unit filter options
          if (employee.department_name && !departmentOptions.includes(employee.department_name)) {
            departmentOptions.push(employee.department_name);
          }
          
          if (statusDisplay && !statusOptions.includes(statusDisplay)) {
            statusOptions.push(statusDisplay);
          }
          
          if (employee.business_unit && !businessUnitOptions.includes(employee.business_unit)) {
            businessUnitOptions.push(employee.business_unit);
          }
          
          // Store time_logs for tertiary access filtering
          return {
            employeeNumber: employeeId,
            zohoId: employee.zoho_id || '',
            employeeName: employee.name || '',
            department: employee.department_name || '',
            status: employee.employee_status || '',
            statusDisplay: statusDisplay,
            businessUnit: employee.business_unit || '',
            location: employee.work_location || '',
            costString: formatCost(employee.cost || '0'),
            cost: employee.cost || 0,
            lastMonthBillableHours: lastMonthBillableHours,
            lastMonthNonBillableHours: lastMonthNonBillableHours,
            comments,
            time_logs: employee.time_logs || [],
            clientNames,
            projectNames,
            timesheetAgingGt10Days: Boolean(employee.timesheet_aging_gt_10_days),
            timesheetAgingGt30Days: Boolean(employee.timesheet_aging_gt_30_days),
            timesheetAgingGt60Days: Boolean(employee.timesheet_aging_gt_60_days),
            timesheetAgingGt90Days: Boolean(employee.timesheet_aging_gt_90_days),
            missingLastWeekTimesheet: Boolean(employee.missing_last_week)
          };
        }));
        
        // Update filter options
        setFilterOptions({
          ...filterOptions,
          department: departmentOptions,
          status: statusOptions,
          clientName: clientNameOptions,
          businessUnit: businessUnitOptions,
          projectName: projectNameOptions
        });
        
        setEmployeeData(transformedEmployees);
        
        // Apply initial filtering based on access level
        if (userInfo.accessLevel === 'tertiary') {
          applyTertiaryFiltering();
        } else if (userInfo.accessLevel === 'secondary' && userInfo.userDepartment) {
          applySecondaryFiltering(filters);
        } else {
          setFilteredData(transformedEmployees);
          setEmployeeCount(transformedEmployees.length);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data', error);
        setError('Failed to fetch employee data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  return (
    <div className="dashboard-container">
      {/* Header/Nav */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Employee Dashboard</h1>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name, ID, department..."
              value={searchText}
              onChange={handleSearchChange}
            />
            <i className="fas fa-search search-icon"></i>
          </div>
          <div className="user-profile" onClick={() => setShowUserDropdown(!showUserDropdown)}>
            <div className="user-avatar">
              {userInfo.name.charAt(0).toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name">{userInfo.name}</div>
              <div className="user-role">
                {userInfo.accessLevel.charAt(0).toUpperCase() + userInfo.accessLevel.slice(1)} Access
              </div>
            </div>
            {showUserDropdown && (
              <div className="user-dropdown">
                <div className="dropdown-item" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i> Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="dashboard-content">
        {/* Filters section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="filter-label" onClick={() => toggleDropdown('department')}>
              Department
              <i className={`fas fa-chevron-${openDropdown === 'department' ? 'up' : 'down'}`}></i>
            </div>
            {openDropdown === 'department' && (
              <div className="filter-dropdown">
                {filterOptions.department.map(dept => (
                  <label key={dept} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.department.includes(dept)}
                      onChange={(e) => handleCheckboxChange('department', dept, e.target.checked)}
                    />
                    {dept}
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <div className="filter-group">
            <div className="filter-label" onClick={() => toggleDropdown('status')}>
              Status
              <i className={`fas fa-chevron-${openDropdown === 'status' ? 'up' : 'down'}`}></i>
            </div>
            {openDropdown === 'status' && (
              <div className="filter-dropdown">
                {filterOptions.status.map(status => (
                  <label key={status} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.status.includes(status)}
                      onChange={(e) => handleCheckboxChange('status', status, e.target.checked)}
                    />
                    {status}
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <div className="filter-group">
            <div className="filter-label" onClick={() => toggleDropdown('businessUnit')}>
              Business Unit
              <i className={`fas fa-chevron-${openDropdown === 'businessUnit' ? 'up' : 'down'}`}></i>
            </div>
            {openDropdown === 'businessUnit' && (
              <div className="filter-dropdown">
                {filterOptions.businessUnit.map(unit => (
                  <label key={unit} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.businessUnit.includes(unit)}
                      onChange={(e) => handleCheckboxChange('businessUnit', unit, e.target.checked)}
                    />
                    {unit}
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <div className="filter-group">
            <div className="filter-label" onClick={() => toggleDropdown('clientName')}>
              Client
              <i className={`fas fa-chevron-${openDropdown === 'clientName' ? 'up' : 'down'}`}></i>
            </div>
            {openDropdown === 'clientName' && (
              <div className="filter-dropdown">
                {filterOptions.clientName.map(client => (
                  <label key={client} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.clientName.includes(client)}
                      onChange={(e) => handleCheckboxChange('clientName', client, e.target.checked)}
                    />
                    {client}
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <div className="filter-group">
            <div className="filter-label" onClick={() => toggleDropdown('projectName')}>
              Project
              <i className={`fas fa-chevron-${openDropdown === 'projectName' ? 'up' : 'down'}`}></i>
            </div>
            {openDropdown === 'projectName' && (
              <div className="filter-dropdown">
                {filterOptions.projectName.map(project => (
                  <label key={project} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.projectName.includes(project)}
                      onChange={(e) => handleCheckboxChange('projectName', project, e.target.checked)}
                    />
                    {project}
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <div className="filter-group">
            <div className="filter-label" onClick={() => toggleDropdown('timesheetAging')}>
              Timesheet Aging
              <i className={`fas fa-chevron-${openDropdown === 'timesheetAging' ? 'up' : 'down'}`}></i>
            </div>
            {openDropdown === 'timesheetAging' && (
              <div className="filter-dropdown">
                <label className="filter-option">
                  <input
                    type="radio"
                    checked={filters.timesheetAging === 'All'}
                    onChange={() => {
                      const newFilters = { ...filters, timesheetAging: 'All' };
                      setFilters(newFilters);
                      if (userInfo.accessLevel === 'tertiary') {
                        applyRegularFiltersToTertiaryData(newFilters);
                      } else if (userInfo.accessLevel === 'secondary') {
                        applySecondaryFiltering(newFilters);
                      } else {
                        applyFiltersAndSearch(searchText, newFilters);
                      }
                    }}
                  />
                  All
                </label>
                {filterOptions.timesheetAging.map(option => {
                  if (option.value === 'All') return null;
                  return (
                    <label key={option.value} className="filter-option">
                      <input
                        type="radio"
                        checked={filters.timesheetAging === option.value}
                        onChange={() => {
                          const newFilters = { ...filters, timesheetAging: option.value };
                          setFilters(newFilters);
                          if (userInfo.accessLevel === 'tertiary') {
                            applyRegularFiltersToTertiaryData(newFilters);
                          } else if (userInfo.accessLevel === 'secondary') {
                            applySecondaryFiltering(newFilters);
                          } else {
                            applyFiltersAndSearch(searchText, newFilters);
                          }
                        }}
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          
          <button className="reset-button" onClick={handleResetFilters}>
            Reset Filters
          </button>
          
          <button className="download-button" onClick={handleDownloadCSV}>
            <i className="fas fa-download"></i> Download CSV
          </button>
        </div>
        
        {/* Employee count display */}
        <div className="employee-count-display">
          {employeeCount} employees found
        </div>
        
        {/* Employee table */}
        <div className="employee-table-container">
          {loading ? (
            <div className="loading-message">Loading employee data...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <table className="employee-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Zoho ID</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Business Unit</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Month Billable</th>
                  <th>Month Non-Billable</th>
                  <th>Cost</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((employee) => (
                  <tr key={employee.employeeNumber}>
                    <td>{employee.employeeName}</td>
                    <td>{employee.zohoId}</td>
                    <td>{employee.department}</td>
                    <td>{employee.statusDisplay}</td>
                    <td>{employee.businessUnit}</td>
                    <td className="client-cell">
                      {employee.clientNames && employee.clientNames.length > 0 
                        ? employee.clientNames.map((client, idx) => (
                            <div key={idx} className="client-item">{client}</div>
                          ))
                        : 'N/A'}
                    </td>
                    <td className="project-cell">
                      {employee.projectNames && employee.projectNames.length > 0 
                        ? employee.projectNames.map((project, idx) => (
                            <div key={idx} className="project-item">{project}</div>
                          ))
                        : 'N/A'}
                    </td>
                    <td>{employee.lastMonthBillableHours}</td>
                    <td>{employee.lastMonthNonBillableHours}</td>
                    <td>${employee.costString}</td>
                    <td>
                      <button
                        className={`comment-button ${employee.comments && employee.comments.length > 0 ? 'has-comments' : ''}`}
                        onClick={() => handleOpenCommentModal(employee)}
                        onMouseOver={(e) => handleMouseOver(e, employee)}
                        onMouseMove={handleMouseMove}
                        onMouseOut={handleMouseOut}
                      >
                        <i className="fas fa-comment"></i>
                        {employee.comments && employee.comments.length > 0 && (
                          <span className="comment-count">{employee.comments.length}</span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
      
      {/* Comment tooltip */}
      {tooltipVisible && hoverEmployee && hoverEmployee.comments && (
        <div 
          className="comment-tooltip" 
          ref={tooltipRef}
          style={{
            left: `${hoverPosition.x}px`,
            top: `${hoverPosition.y}px`
          }}
        >
          <div className="tooltip-header">
            <h3>Recent Comments</h3>
          </div>
          <div className="tooltip-content">
            {hoverEmployee.comments.slice(-3).map((comment, index) => (
              <div key={index} className="tooltip-comment">
                <div className="tooltip-comment-text">{comment.text}</div>
                <div className="tooltip-comment-meta">
                  <span className="tooltip-comment-author">{comment.authorName || comment.author || 'Anonymous'}</span>
                  <span className="tooltip-comment-time">
                    {new Date(comment.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="tooltip-footer">
            {hoverEmployee.comments.length > 3 && (
              <span>+ {hoverEmployee.comments.length - 3} more comments</span>
            )}
          </div>
        </div>
      )}
      
      {/* Comment modal */}
      <CommentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        employee={selectedEmployee}
        onSubmit={handleSubmitComment}
        commentText={commentText}
        setCommentText={setCommentText}
        submitting={commentSubmitting}
      />
    </div>
  );
}